import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, Award, Users, TrendingUp, Sparkles, Quote, Star, GraduationCap, Settings, SearchCheck, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import CourseCard from "@/components/CourseCard";

const CATEGORIES = [
  { key: "Ön Muhasebe", desc: "Fatura, cari, stok, e-belge", icon: "01" },
  { key: "Genel Muhasebe", desc: "Yevmiye, mizan, mali tablo", icon: "02" },
  { key: "Beyanname", desc: "KDV, muhtasar, kurumlar", icon: "03" },
  { key: "İleri Seviye", desc: "Dönem sonu, vergi planlama", icon: "04" },
];

const CORPORATE_SERVICES = [
  {
    icon: GraduationCap,
    title: "Bire Bir Personel Eğitimi",
    desc: "Muhasebe personelinize, işletmenizin kullandığı program ve iş akışları üzerinden uygulamalı eğitim.",
  },
  {
    icon: Settings,
    title: "Muhasebe Sistem Kurulumu",
    desc: "Cari, stok, fatura, kasa, banka ve muhasebe süreçlerinin işletmeye uygun şekilde yapılandırılması.",
  },
  {
    icon: SearchCheck,
    title: "Süreç & Risk Analizi",
    desc: "Mevcut muhasebe süreçlerinin incelenmesi, aksayan noktaların ve operasyonel risklerin belirlenmesi.",
  },
  {
    icon: Briefcase,
    title: "Dışarıdan Danışmanlık",
    desc: "İşletmenin ihtiyaçlarına göre muhasebe süreçleri ve operasyonları konusunda profesyonel destek.",
  },
];

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/courses", { params: { featured: true } }).then((r) => setCourses(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <>
      {/* HERO */}
      <section data-testid="hero" className="relative overflow-hidden noise-overlay">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1761437856376-2ce1c483343b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
        </div>

        <div className="container-x relative pt-24 pb-32 md:pt-32 md:pb-40 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-px bg-gold" />
                <span className="section-eyebrow">Zülfikar Benli ile</span>
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white">
                Muhasebede
                <br />
                <span className="italic gold-gradient-text">ustalaşmanın</span>
                <br />
                yeni adresi.
              </h1>
              <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed">
                Ön muhasebeden ileri seviye vergi planlamaya, sahada işe yarayan
                gerçek bilgiyle donatılmış premium eğitim serisi. Kariyerinizi bir
                sonraki seviyeye taşıyın.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link
                  to="/egitimler"
                  data-testid="hero-cta-primary"
                  className="btn-primary"
                >
                  Eğitimleri İncele <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="https://www.youtube.com/@zbcampus"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-cta-youtube"
                  className="btn-outline"
                >
                  <PlayCircle className="w-4 h-4" /> Ücretsiz Dersi İzle
                </a>
              </div>

              <div className="mt-16 grid grid-cols-3 gap-8 max-w-lg">
                <div>
                  <div className="font-display text-3xl text-gold">25+</div>
                  <div className="text-xs uppercase tracking-widest text-white/50 mt-1">Yıl Sektör Deneyimi</div>
                </div>
                <div>
                  <div className="font-display text-3xl text-gold">15+</div>
                  <div className="text-xs uppercase tracking-widest text-white/50 mt-1">Yıl Eğitmenlik</div>
                </div>
                <div>
                  <div className="font-display text-3xl text-gold">500+</div>
                  <div className="text-xs uppercase tracking-widest text-white/50 mt-1">Öğrenci</div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="hidden lg:block lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative border border-gold-30 p-8 bg-black/60 backdrop-blur-md">
                <Sparkles className="w-6 h-6 text-gold mb-4" />
                <div className="section-eyebrow mb-3">Bu Ay</div>
                <div className="font-display text-2xl text-white mb-1">
                  Temelden İleri Seviyeye
                </div>
                <div className="font-display text-2xl text-gold mb-3">
                  Genel Muhasebe Eğitimi
                </div>
                <div className="inline-block text-[11px] uppercase tracking-widest text-gold bg-gold/10 border border-gold-30 px-3 py-1 mb-4">
                  Ağustos Dönemi Kayıtları Başladı
                </div>
                <p className="text-sm text-white/70 mb-6">
                  30 saatlik uygulamalı canlı ders, Tek Düzen Hesap Planı, mizan ve dönem sonu işlemleriyle iş hayatına hazırlık. Kontenjan sınırlıdır.
                </p>
                <Link to="/egitim/genel-muhasebe-egitimi" className="btn-outline text-sm w-full justify-center">
                  Programı Gör <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="absolute -top-4 -left-4 w-24 h-24 border border-gold-30 -z-10" />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-gold-30 -z-10" />
            </motion.div>
          </div>
        </div>

        <div className="hairline" />
      </section>

      {/* CATEGORIES */}
      <section data-testid="categories" className="container-x py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
          <div className="lg:col-span-7">
            <span className="section-eyebrow">Uzmanlık Alanları</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
              İhtiyacınıza göre <span className="italic gold-gradient-text">özel</span> hazırlanmış programlar.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link to="/egitimler" className="btn-outline text-sm">
              Tüm Kataloğu Gör <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CATEGORIES.map((cat, i) => (
            <motion.div
              key={cat.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <Link
                to={`/egitimler?kategori=${encodeURIComponent(cat.key)}`}
                data-testid={`category-card-${cat.key}`}
                className="card-hover block bg-surface border border-white/10 p-8 h-full group"
              >
                <div className="font-display text-5xl text-gold/40 group-hover:text-gold transition-colors mb-6">
                  {cat.icon}
                </div>
                <h3 className="font-display text-xl mb-2">{cat.key}</h3>
                <p className="text-sm text-white/60">{cat.desc}</p>
                <div className="mt-6 text-xs uppercase tracking-widest text-gold flex items-center gap-2">
                  Programları Gör <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED COURSES */}
      <section data-testid="featured-courses" className="container-x py-16">
        <div className="mb-14">
          <span className="section-eyebrow">Öne Çıkan Eğitimler</span>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-2xl">
            Şu anda en çok tercih edilen <span className="italic gold-gradient-text">programlar</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.slice(0, 6).map((c) => (
            <CourseCard key={c.id} course={c} />
          ))}
        </div>
      </section>

      {/* CORPORATE SOLUTIONS */}
      <section data-testid="corporate-solutions" className="container-x py-16">
        <div className="border border-gold-30 bg-surface p-8 md:p-14">
          <span className="section-eyebrow">Kurumsal Çözümler</span>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight max-w-2xl">
            İşletmenize Özel <span className="italic gold-gradient-text">Muhasebe Çözümleri</span>.
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">
            Standart eğitim yerine, işletmenizin ihtiyaçlarına göre şekillenen personel eğitimi,
            muhasebe sistem kurulumu, süreç analizi ve danışmanlık çözümleri.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {CORPORATE_SERVICES.map((s) => (
              <div key={s.title} className="bg-black/40 border border-white/10 p-6">
                <s.icon className="w-7 h-7 text-gold mb-4" />
                <h3 className="font-display text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link to="/kurumsal-cozumler" className="btn-primary text-sm">
              Kurumsal Çözümleri İncele <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SOFTWARE */}
      <section className="container-x py-16">
        <div className="border border-white/10 bg-surface p-8 md:p-10">
          <span className="section-eyebrow">Uygulamalı Eğitim</span>
          <h3 className="font-display text-2xl md:text-3xl mt-3 mb-6">
            Sektörün kullandığı <span className="italic gold-gradient-text">yazılımlarla</span> çalışıyoruz.
          </h3>
          <div className="flex flex-wrap gap-3">
            <span className="px-5 py-2.5 border-2 border-gold bg-gold/10 text-gold text-sm font-medium tracking-wide">
              Luca
            </span>
            {["Logo GO3 / Tiger", "Mikro", "Zirve", "Dia", "Orka", "Nebim V3", "Uyumsoft"].map((name) => (
              <span key={name} className="px-5 py-2.5 border border-white/15 text-white/70 text-sm">
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="container-x py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] max-w-md">
            <img
              src="/images/zulfikar-benli.webp"
              alt="Zülfikar Benli"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 border border-gold-30 -m-4 -z-10" />
            <div className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-md border border-gold-30 p-5">
              <div className="section-eyebrow mb-2">Kurucu Eğitmen</div>
              <div className="font-display text-2xl">Zülfikar Benli</div>
              <div className="text-sm text-white/60 mt-1">25+ yıl sektör, 15+ yıl eğitmenlik deneyimi</div>
            </div>
          </div>

          <div>
            <span className="section-eyebrow">Neden ZB Campus?</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
              Kitap bilgisi değil,
              <br />
              <span className="italic gold-gradient-text">saha</span> deneyimi.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              15 yıl aktif mali müşavirlik ve müşavirlik ofisi yönetimi tecrübesiyle,
              müfredatın her satırı gerçek işyerinde karşılaşacağınız senaryolara göre
              hazırlandı. Anladığınızdan emin olana kadar yanınızdayız.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Feature icon={<Award className="w-5 h-5" />} title="Sertifikalı" desc="Tamamlayan her öğrenci kişiye özel sertifika alır." />
              <Feature icon={<Users className="w-5 h-5" />} title="Küçük Gruplar" desc="Bire bir dikkatle daha etkili öğrenme." />
              <Feature icon={<TrendingUp className="w-5 h-5" />} title="Kariyer Odaklı" desc="İş görüşmesine hazır bilgi seviyesi." />
              <Feature icon={<Sparkles className="w-5 h-5" />} title="Güncel Mevzuat" desc="Her dönem güncellenen içerikler." />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section data-testid="testimonials" className="container-x py-24">
          <div className="mb-14 max-w-2xl flex items-end justify-between gap-6 flex-wrap">
            <div>
              <span className="section-eyebrow">Öğrenci Yorumları</span>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                Onların hikayeleri, <span className="italic gold-gradient-text">yeni</span> başlangıçlar.
              </h2>
            </div>
            {testimonials.length > 6 && (
              <Link to="/yorumlar" className="text-sm text-gold hover:text-gold/80 whitespace-nowrap">
                Tümünü Gör ({testimonials.length}) →
              </Link>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-surface border border-white/10 p-8 relative card-hover"
              >
                <Quote className="w-8 h-8 text-gold/30 mb-4" />
                <p className="text-white/85 leading-relaxed italic">"{t.quote}"</p>
                <div className="hairline my-5" />
                <div className="flex items-center gap-3">
                  {t.avatar_url && (
                    <img src={t.avatar_url} alt={t.name} className="w-12 h-12 rounded-full object-cover border border-gold-30" />
                  )}
                  <div>
                    <div className="font-display text-lg">{t.name}</div>
                    <div className="text-xs text-white/50">{t.role}</div>
                  </div>
                  <div className="ml-auto flex items-center gap-0.5">
                    {[...Array(t.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-gold text-gold" />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* CTA STRIP */}
      <section className="relative overflow-hidden">
        <div className="container-x py-20">
          <div className="border border-gold-30 bg-surface p-10 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-gold blur-[120px]" />
            </div>
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="section-eyebrow">Bir Sonraki Adım</span>
                <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight">
                  Kariyerinize bugün başlayın.
                </h2>
                <p className="text-white/70 mt-5 max-w-xl">
                  Ücretsiz tanıtım derslerini izleyin, kendinize uygun programı seçin
                  ve ZB Campus mezunları arasına katılın.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row md:justify-end gap-4">
                <Link to="/egitimler" className="btn-primary">Eğitimleri Gör <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/iletisim" className="btn-outline">Bize Ulaşın</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 shrink-0 border border-gold-30 flex items-center justify-center text-gold">
        {icon}
      </div>
      <div>
        <div className="font-display text-lg">{title}</div>
        <div className="text-sm text-white/60 mt-1">{desc}</div>
      </div>
    </div>
  );
}
